import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { NextRouter } from "next/router"

import { buildWorkspaceAwareNavigation } from "@/utils/workspaceRoutes"

import type { FilterState } from "../types/filterState"
import type { RunsSavedView } from "../types/savedViews"

interface RunsSavedViewsClient {
  fetchUserDefaultView: () => Promise<RunsSavedView | null>
  fetchView: (ulid: string) => Promise<RunsSavedView | null>
}

interface RunsViewToastOptions {
  title: string
  description?: string
  color?: "danger"
}

interface UseRunsViewStateParams {
  isReady: boolean
  router: Pick<NextRouter, "pathname" | "asPath" | "query" | "replace">
  state: FilterState
  rawSetFields: (fields: Partial<FilterState>) => void
  savedViewsClient: RunsSavedViewsClient
  showToast: (options: RunsViewToastOptions) => void
}

export function useRunsViewState({
  isReady,
  router,
  state,
  rawSetFields,
  savedViewsClient,
  showToast,
}: UseRunsViewStateParams) {
  const [loadedViewId, setLoadedViewId] = useState<string | null>(null)
  const hasBootstrappedRef = useRef(false)
  const requestGenerationRef = useRef(0)
  const isMountedRef = useRef(false)
  const stateRef = useRef(state)

  stateRef.current = state

  const clearLoadedView = useCallback(
    (options?: { updateUrl?: boolean }) => {
      requestGenerationRef.current += 1
      setLoadedViewId(null)

      if (options?.updateUrl !== false && router.query.viewId) {
        const nextQuery = { ...router.query }
        delete nextQuery.viewId

        const navigation = buildWorkspaceAwareNavigation(
          router.asPath,
          router.pathname,
          nextQuery,
        )
        void router.replace(navigation.href, navigation.as, { shallow: true })
      }
    },
    [router],
  )

  const applyViewConfigToState = useCallback(
    (configFilters: RunsSavedView["config"]["filters"] | undefined) => {
      if (!configFilters) {
        return
      }

      const nextState: Partial<FilterState> = {
        searchValue: configFilters.searchValue ?? "",
        dateRange: {
          start: configFilters.startDate
            ? new Date(configFilters.startDate)
            : null,
          end: configFilters.endDate ? new Date(configFilters.endDate) : null,
        },
        statusFilters: configFilters.statusFilters ?? [],
        templateFilters: configFilters.templateFilters ?? [],
        personaFilters: configFilters.personaFilters ?? [],
        agentFilters: configFilters.agentFilters ?? [],
        metricFilters: configFilters.metricFilters ?? [],
        tagFilters: configFilters.tagFilters ?? [],
        mutationFilters: configFilters.mutationFilters ?? [],
        testSetFilters: configFilters.testSetFilters ?? [],
        testCaseFilters: configFilters.testCaseFilters ?? [],
        metadataFilters: configFilters.metadataFilters ?? [],
        scheduledJobFilters: configFilters.scheduledJobFilters ?? [],
      }

      if (configFilters.sortDescriptor) {
        nextState.sortDescriptor = {
          column: configFilters.sortDescriptor.column,
          direction: configFilters.sortDescriptor.direction,
        }
      }

      rawSetFields(nextState)
    },
    [rawSetFields],
  )

  const applyView = useCallback(
    (view: RunsSavedView) => {
      requestGenerationRef.current += 1
      applyViewConfigToState(view.config.filters)
      setLoadedViewId(view.ulid)

      const nextQuery = { ...router.query, viewId: view.ulid }
      const navigation = buildWorkspaceAwareNavigation(
        router.asPath,
        router.pathname,
        nextQuery,
      )
      void router.replace(navigation.href, navigation.as, { shallow: true })
    },
    [applyViewConfigToState, router],
  )

  const isStatePristine = useCallback((current: FilterState): boolean => {
    return (
      current.searchValue === "" &&
      current.dateRange.start === null &&
      current.dateRange.end === null &&
      current.statusFilters.length === 0 &&
      current.templateFilters.length === 0 &&
      current.personaFilters.length === 0 &&
      current.agentFilters.length === 0 &&
      current.metricFilters.length === 0 &&
      current.tagFilters.length === 0 &&
      current.mutationFilters.length === 0 &&
      current.testSetFilters.length === 0 &&
      current.testCaseFilters.length === 0 &&
      (current.metadataFilters?.length ?? 0) === 0 &&
      current.scheduledJobFilters.length === 0
    )
  }, [])

  useEffect(() => {
    if (!isReady || hasBootstrappedRef.current) {
      return
    }

    hasBootstrappedRef.current = true
    const requestGeneration = requestGenerationRef.current

    const viewIdParam = router.query.viewId

    if (typeof viewIdParam === "string" && viewIdParam.length > 0) {
      void savedViewsClient
        .fetchView(viewIdParam)
        .then((view) => {
          if (
            !isMountedRef.current ||
            requestGeneration !== requestGenerationRef.current
          ) {
            return
          }
          if (!view) {
            showToast({
              title: "View not available",
              description:
                "This view may have been deleted or you don't have access.",
              color: "danger",
            })

            const nextQuery = { ...router.query }
            delete nextQuery.viewId

            const navigation = buildWorkspaceAwareNavigation(
              router.asPath,
              router.pathname,
              nextQuery,
            )
            void router.replace(navigation.href, navigation.as, {
              shallow: true,
            })

            return
          }

          applyViewConfigToState(view.config.filters)
          setLoadedViewId(view.ulid)
        })
        .catch(() => {
          if (
            !isMountedRef.current ||
            requestGeneration !== requestGenerationRef.current
          ) {
            return
          }
          showToast({
            title: "Failed to load view",
            color: "danger",
          })
        })

      return
    }

    if (!isStatePristine(stateRef.current)) {
      return
    }

    void savedViewsClient
      .fetchUserDefaultView()
      .then((view) => {
        if (
          !isMountedRef.current ||
          requestGeneration !== requestGenerationRef.current ||
          !view ||
          !isStatePristine(stateRef.current)
        ) {
          return
        }

        applyViewConfigToState(view.config.filters)
        setLoadedViewId(view.ulid)
      })
      .catch(() => {
        // Default view loading is best-effort.
      })
  }, [
    applyViewConfigToState,
    isReady,
    isStatePristine,
    router,
    savedViewsClient,
    showToast,
  ])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return useMemo(
    () => ({
      loadedViewId,
      applyView,
      clearLoadedView,
    }),
    [applyView, clearLoadedView, loadedViewId],
  )
}
